/* eslint-disable no-unused-vars */
import { useContext } from 'react';
import { useSignal } from '@preact/signals-react';
import { useSelector } from 'react-redux';
import { selectPb_username, isAdminSelector } from 'store/selectors';
import { ManageCampaignsContext } from 'contexts';

const campaignSettings = [
  { fieldName: 'campaign', title: 'Campaign Name', isSelected: true, isDisabled: true },
  { fieldName: 'campaignType', title: 'Campaign Type', isSelected: true, isDisabled: true },
  { fieldName: 'entityType', title: 'Entity Type', isSelected: true },
  { fieldName: 'bid_type', title: 'Bid Type', isSelected: true },
  { fieldName: 'bid', title: 'Default Bid' },
  { fieldName: 'budget', title: 'Daily Budget' },
  { fieldName: 'packageName', title: 'Promoted app', isSelected: true },
  { fieldName: 'countries', title: 'Targeted Locations', isSelected: true },
];

const metrics = [
  { fieldName: 'installs', title: 'Installs' },
  { fieldName: 'impressions', title: 'Viewable Impressions' },
  { fieldName: 'clicks', title: 'Clicks' },
  { fieldName: 'cost', title: 'Cost' },
  { fieldName: 'cr', title: 'Conv. Rate' },
  { fieldName: 'avg_cpi', title: 'AVG CPI' },
  { fieldName: 'ecpm', title: 'eCPM' },
  { fieldName: 'video_views', title: 'Video Views' },
  { fieldName: 'video_completed', title: 'Video Ended' },
  { fieldName: 'ctr', title: 'CTR' },
];

let isExported = false;

export const useFacade = (isOpen$) => {
  const { colDefs, rowData } = useContext(ManageCampaignsContext);

  const username = useSelector(selectPb_username);
  const dateInStore = useSelector((state) => state.manageCampaigns.selectedDateRange);
  const isAdmin = useSelector(isAdminSelector);

  const startDate = dateInStore?.from.replaceAll('/', '-');
  const endDate = dateInStore?.to.replaceAll('/', '-');

  const campaignSettings$ = useSignal(getSettings(campaignSettings, colDefs));
  const metrics$ = useSignal(getMertics(metrics, colDefs));
  const events$ = useSignal(getEvents(colDefs));

  const isAllCampaignSelected$ = useSignal(isAnySelected(campaignSettings$.peek()));
  const isAllMetricsSelected$ = useSignal(isAnySelected(metrics$.peek()));
  const isAllEventsSelected$ = useSignal(isAnySelected(events$.peek()));

  const isLoading$ = useSignal(false);

  function handleSave() {
    setTimeout(() => {
      if (!isExported) {
        isLoading$.value = true;
        setTimeout(() => (isLoading$.value = false), 3000);
      }
    }, 3000);

    const keysObj = [...campaignSettings$.peek(), ...metrics$.peek(), ...events$.peek()];
    const keys = keysObj.filter((el) => el.isSelected).map((obj) => obj.fieldName);

    const selectedData = rowData.map((item) => {
      const base = keys.reduce((acc, key) => {
        if (key === 'entityType') {
          acc[key] = item.isWebsite ? 'Website' : 'App';
          return acc;
        }
        if (!!item[key] || item[key] === 0 || item[key] === '') {
          acc[key] = item[key];
        }
        return acc;
      }, {});

      return { ...base, biddingCountries: item.biddingCountries };
    });

    const csvContent = convertToCSV(selectedData, isAdmin);
    const fileName = `Campaigns for ${username} - ${startDate} to ${endDate}.csv`;
    downloadCSV(csvContent, fileName);
    isOpen$.value = false;
  }

  function handleCampaign(fieldName) {
    let newValue;

    campaignSettings$.value = campaignSettings$.peek().map((el) => {
      if (el.fieldName === fieldName) {
        newValue = !el.isSelected;
        el.isSelected = newValue;
      }
      return el;
    });

    if (newValue) {
      isAllMetricsSelected$.value = true;
    }
  }

  function handleCampaignAll(value = undefined) {
    const newValue = !isAllCampaignSelected$.value;

    isAllCampaignSelected$.value = newValue;

    if (value !== undefined) {
      campaignSettings$.value = campaignSettings$.peek().map((el) => {
        if (el.isDisabled) return el;
        el.isSelected = newValue;
        return el;
      });
    }
  }

  function handleMetrics(fieldName) {
    let newValue;

    metrics$.value = metrics$.peek().map((el) => {
      if (el.fieldName === fieldName) {
        newValue = !el.isSelected;
        el.isSelected = newValue;
      }
      return el;
    });

    if (newValue) {
      isAllMetricsSelected$.value = true;
    }
  }

  function handleMetricsAll(value = undefined) {
    const newValue = value ? value : !isAllMetricsSelected$.value;

    isAllMetricsSelected$.value = newValue;

    if (value !== undefined) {
      metrics$.value = metrics$.peek().map((el) => {
        el.isSelected = newValue;
        return el;
      });
    }
  }

  function handleEvents(fieldName) {
    let newValue;

    events$.value = events$.peek().map((el) => {
      if (el.fieldName === fieldName) {
        newValue = !el.isSelected;
        el.isSelected = newValue;
      }
      return el;
    });

    if (newValue) {
      isAllMetricsSelected$.value = true;
    }
  }

  function handleEventsAll(value = undefined) {
    const newValue = value ? value : !isAllEventsSelected$.value;

    isAllEventsSelected$.value = newValue;

    if (value !== undefined) {
      events$.value = events$.peek().map((el) => {
        el.isSelected = newValue;
        return el;
      });
    }
  }

  return {
    campaignSettings$,
    metrics$,
    events$,
    isAllCampaignSelected$,
    isAllMetricsSelected$,
    isAllEventsSelected$,
    isLoading$,
    handleSave,
    handleCampaignAll,
    handleCampaign,
    handleMetrics,
    handleMetricsAll,
    handleEvents,
    handleEventsAll,
  };
};

function isCpm(obj, isAdmin) {
  return (
    obj.bid_type === 3 &&
    !obj.isOnBoarding &&
    obj.campaignType === 'User acquisition' &&
    isAdmin &&
    obj.countries !== '' &&
    !obj.isAllPlacements &&
    obj.biddingCountries?.length > 0
  );
}

function getEvents(colDefs) {
  const res = colDefs
    .find((colDef) => colDef.headerName === 'Events')
    ?.children?.map(({ colId, hide }) => {
      const title = colId?.split('_').slice(1, -1).join('_');
      const isSelected = !!(colId && !hide);

      return colId ? { fieldName: colId, title, isSelected } : null;
    })
    .filter((item) => item !== null)
    .sort((a, b) => a.title.localeCompare(b.title)); // to handle changing order

  return res || [];
}

function getMertics(metrics, colDefs) {
  const res = metrics.map((item) => {
    const colElem = colDefs.find((el) => el.colId === item.fieldName);
    const isSelected = item.isDisabled
      ? item.isSelected
      : item.isSelected
      ? item.isSelected
      : !!colElem?.colId && !colElem?.hide;

    return { ...item, isSelected };
  });

  return res || [];
}

function getSettings(campaignSettings, colDefs) {
  const res = campaignSettings.map((item) => {
    const colElem = colDefs.find((el) => el.colId === item.fieldName);

    const isSelected = item.isDisabled
      ? item.isSelected
      : item.isSelected ||
        (!!colElem?.colId && !colElem?.hide) ||
        (colElem?.lockPosition && !!colElem?.colId);

    return { ...item, isSelected };
  });

  return res || [];
}

function isAnySelected(data) {
  return data.some((item) => item.isSelected);
}

function convertToCSV(objArray, isAdmin) {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : structuredClone(objArray);
  let csv = '';

  const columns = Object.keys(array[0]).filter((el) => el !== 'biddingCountries');

  const formatedHeader = columns.map((header) => {
    let newHeader = header;
    switch (header) {
      case 'campaignType':
        newHeader = 'Campaign Type';
        break;
      case 'bid_type':
        newHeader = 'Bid Type';
        break;
      case 'packageName':
        newHeader = 'Promoted App';
        break;
      case 'cr':
        newHeader = 'Conv. Rate';
        break;
      case 'avg_cpi':
        newHeader = 'AVG CPI';
        break;
      case 'ecpm':
        newHeader = 'eCPM';
        break;
      case 'ctr':
        newHeader = 'CTR';
        break;
      case 'entityType':
        newHeader = 'Entity Type';
        break;
      case 'countries':
        newHeader = 'Targeted Locations';
        break;
      case 'video_views':
        newHeader = 'Video Views';
        break;
      case 'video_completed':
        newHeader = 'Video Ended';
        break;
      default:
        newHeader = header.includes('events_')
          ? header.split('_').slice(1, -1).join('_')
          : header.charAt(0).toUpperCase() + header.slice(1);
    }

    return newHeader;
  });
  csv += formatedHeader.join(',') + '\n';

  const formatedArray = array.map((item) => {
    // truncate numbers and add thousands separators to match the UI (e.g. 1,250)
    for (const key in item) {
      // bid_type is a numeric code that isCpm() compares with === , keep it a number
      if (key === 'bid_type') continue;
      if (typeof item[key] === 'number') {
        item[key] = parseFloat(item[key].toFixed(3)).toLocaleString('en-US', {
          maximumFractionDigits: 3,
        });
      }
    }

    // add symbols
    item.campaign = isCpm(item, isAdmin) ? item.campaign + ' – CPM' : item.campaign;
    item.cost = '$' + item.cost;
    item.bid = '$' + item.bid;
    item.budget = '$' + item.budget;
    item.cr = (item.cr && item.cr != 'Infinity' ? item.cr : 0) + '%';
    item.ctr = (item.ctr && item.ctr != 'Infinity' ? item.ctr : 0) + '%';
    item.bid_type =
      item.bid_type == 1 ? 'CPM' : item.bid_type == 2 ? 'CPC' : item.bid_type == 3 ? 'CPI' : '';
    item.countries = item.countries !== '' ? item.countries : 'All Countries';

    delete item.biddingCountries;

    return item;
  });

  // data rows
  for (const row of formatedArray) {
    const values = columns.map((header) => {
      const escaped = String(row[header]).replace(/"/g, '\\"');
      return `"${escaped}"`;
    });
    csv += values.join(',') + '\n';
  }

  return csv;
}

function downloadCSV(content, filename) {
  const utf8BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
  const blob = new Blob([utf8BOM, content], { type: 'text/csv;charset=UTF-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  isExported = true;
}
